import { useCssVars as _useCssVars, unref as _unref, defineComponent as _defineComponent } from 'vue'
import { computed } from "@vue/reactivity";
import { PropType, inject, ref, watch } from "vue";
import { Field, FieldMeta } from "vee-validate";
import { useDeviceStore } from "@/stores/deviceStore";
import DynamicSelect from "./DynamicSelect.vue";
import {
  IOption,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_TOAST_MESSAGE,
  ShowErrorModal,
  ShowToastMessage,
  TimerStatus,
} from "@/types";
import { useUserStore } from "@/stores/userStore";
import DOMPurify from "dompurify";


const __default__ = {
  name: "EmailInput",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  label: {
    type: String,
    default: "",
  },
  name: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    default: "",
  },
  value: {
    type: String,
  },
  errorMessage: {
    type: String,
    default: "",
  },
  successMessage: {
    type: String,
    default: "",
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  certRequired: {
    type: Boolean,
    default: false,
  },
  parentKey: {
    // parentKey가 있으면 domain selectbox를 보여줘야 한다.
    type: String,
    default: "",
  },
  required: {
    type: Boolean,
    default: true,
  },
  options: {
    type: Array as PropType<IOption[]>,
  },
  rules: {
    type: Object,
  },
  isEdited: {
    type: Boolean,
    default: false,
  },
},
  emits: ["updateVerficationCodeSent", "update:value", "onUpdatedOptions", "updateChildTimerStatus", "updateChildFieldValidity"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

_useCssVars(_ctx => ({
  "14a5750c-inputTextWidthStr": (_unref(inputTextWidthStr))
}))

const props = __props;

const emit = __emit;

const inputValue = computed({
  get(): string {
    const value = unFormattingEmail(props.value);
    return value;
  },
  set(inputValue: string) {
    const value = formattingEmail(inputValue);
    emit("update:value", value);
  },
});

const emailValue = computed({
  get(): string {
    return props.value ?? "";
  },
  set() {
    //
  },
});

const arrangedOptions = computed(() => {
  return props.options?.map((option) =>
    !option.value
      ? { ...option, name: DIRECT_INPUT_STR, value: DIRECT_INPUT_STR }
      : { ...option, name: `@${option.name}` }
  );
});

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const showToastMessage = inject(
  PROVIDE_KEY_SHOW_TOAST_MESSAGE
) as ShowToastMessage;

const deviceStore = useDeviceStore();
const userStore = useUserStore();

const inputField = ref<HTMLInputElement>();
const focusingInputField = ref<HTMLInputElement>();
const isFocused = ref(false);
const buttonTitle = ref("인증요청");

const inputTextWidth = ref(0);
const inputTextWidthStr = computed(() => inputTextWidth.value + "px");
const inputInnerSpan = ref<HTMLElement>();

const DIRECT_INPUT_STR = "직접입력";

const isError = (
  meta: FieldMeta<any>,
  validationErrorMsg: string | undefined
) => {
  return !!props.errorMessage || (meta.touched && !!validationErrorMsg);
};

const domainValue = ref("");
const formattedDomain = computed(() =>
  domainValue.value ? `@${domainValue.value}` : ""
);
const onChangeDomain = () => {
  if (!inputField.value) return;
  inputValue.value = unFormattingEmail(inputField.value.value);
};

const setDomainValue = (inputValue?: string) => {
  if (!inputValue) return;
  const index = inputValue.indexOf("@");
  domainValue.value = inputValue.slice(index + 1);
};

const formattingEmail = (value: string) => {
  if (!props.parentKey || domainValue.value === DIRECT_INPUT_STR) {
    return value;
  }
  return `${value}${formattedDomain.value}`;
};

const unFormattingEmail = (value?: string) => {
  if (!value) return "";
  if (!props.parentKey || domainValue.value === DIRECT_INPUT_STR) {
    return value;
  }
  const index = value.indexOf("@");
  const inputValue = index > -1 ? value.slice(0, index) : value;
  return inputValue;
};

const focusInputField = () => {
  inputField.value?.focus();
};

const onFocused = (event: Event) => {
  focusingInputField.value = event.target as HTMLInputElement;
  isFocused.value = true;
};

const onBlurred = () => {
  focusingInputField.value = undefined;
  isFocused.value = false;
};

const onChange = (event: Event) => {
  const inputElement = event.target as HTMLInputElement;
  const sanitizedValue = DOMPurify.sanitize(inputElement.value);
  inputValue.value = sanitizedValue;
};

const onInput = (event: Event) => {
  const inputElement = event.target as HTMLInputElement;
  updateInputInnerText(inputElement.value);

  if (props.parentKey && domainValue.value !== DIRECT_INPUT_STR) {
    inputElement.value = inputElement.value.replace(/@/g, "");
  }
  const sanitizedValue = DOMPurify.sanitize(inputElement.value);
  inputValue.value = sanitizedValue;
  inputElement.value = sanitizedValue;
};

const shouldShowClearButton = computed(
  () => inputValue.value.length > 0 && isFocused.value
);

const updateInputInnerText = (text: string) => {
  if (!inputInnerSpan.value) return;

  const sanitizedValue = DOMPurify.sanitize(text);
  inputInnerSpan.value.innerText = sanitizedValue;
  inputTextWidth.value = inputInnerSpan.value.clientWidth;
};

const clearText = () => {
  if (!inputValue.value) {
    return;
  }
  const blank = "";
  emit("update:value", blank);
  focusInputField();
  updateInputInnerText(blank);
};

// 이메일 인증번호 요청하기
const requestEmailVerification = async () => {
  if (!props.value) return;
  emit("updateVerficationCodeSent", true);
  buttonTitle.value = "재요청";

  const requestEmailVerificationResult =
    await userStore.sendEmailVerificationCode(props.value);

  if (requestEmailVerificationResult.isSuccess) {
    emit("updateChildFieldValidity", true);
    emit("updateChildTimerStatus", TimerStatus.START);
    showToastMessage("인증번호가 발송되었습니다");
    return;
  }

  showErrorModal(
    "이메일 인증요청 오류",
    requestEmailVerificationResult.errorMessage,
    requestEmailVerificationResult.resultCode,
    requestEmailVerificationResult.errorTime
  );
};

watch(
  () => deviceStore.isShowingKeyboard,
  (isShowing, _) => {
    if (!isShowing) {
      return;
    }

    focusingInputField.value?.scrollIntoView({
      block: "center",
      behavior: "smooth",
    });
  }
);

watch(
  () => props.value,
  (value, preValue) => {
    const isEmailFormat = value?.includes("@");
    if (preValue === undefined && isEmailFormat) {
      setDomainValue(value);
    }
  }
);

watch(
  () => arrangedOptions.value,
  (options) => {
    if (!formattedDomain.value) return;
    const isHasDomain = options?.some(
      (option) => option.name === formattedDomain.value
    );
    domainValue.value = isHasDomain
      ? formattedDomain.value.slice(1)
      : DIRECT_INPUT_STR;
  }
);

const __returned__ = { props, emit, inputValue, emailValue, arrangedOptions, showErrorModal, showToastMessage, deviceStore, userStore, inputField, focusingInputField, isFocused, buttonTitle, inputTextWidth, inputTextWidthStr, inputInnerSpan, DIRECT_INPUT_STR, isError, domainValue, formattedDomain, onChangeDomain, setDomainValue, formattingEmail, unFormattingEmail, focusInputField, onFocused, onBlurred, onChange, onInput, shouldShowClearButton, updateInputInnerText, clearText, requestEmailVerification, get Field() { return Field }, DynamicSelect }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})